import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Trash2, UserPlus, Users } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SelectField } from "@/components/SelectField";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("arihant_admin_token");
  const role = localStorage.getItem("arihant_admin_role") || "admin";
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users", { headers });
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Admin role required for user management.");
        navigate("/admin");
      } else toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    if (role !== "admin") { toast.error("Admin role required for user management."); navigate("/admin"); return; }
    fetchUsers();
  }, [token, role, navigate, fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", form, { headers });
      toast.success(`User ${form.email} created (${form.role}).`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create user.");
    } finally {
      setCreating(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`, { headers });
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("User removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete user.");
    }
  };

  const logout = () => {
    localStorage.removeItem("arihant_admin_token");
    localStorage.removeItem("arihant_admin_role");
    navigate("/admin/login");
  };

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid="admin-users-page">
      <header className="border-b border-gold/15 bg-surface/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-users-logo">
            <BrandMark size={36} iconSize={16} />
            <span className="font-display text-xl text-cream">
              <BrandName />
              <span className="text-xs font-mono tracking-widest text-faded ml-3 uppercase">{role}</span>
            </span>
          </Link>
          <nav className="flex items-center gap-3 flex-wrap">
            <Link to="/admin" data-testid="admin-tab-enquiries" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Enquiries</Link>
            <Link to="/admin/packages" data-testid="admin-tab-packages" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Packages</Link>
            <span data-testid="admin-tab-users" className="text-xs text-ink bg-gold px-5 py-2.5 rounded-full font-semibold">Users</span>
            {role === "admin" && (
              <Link to="/admin/settings" data-testid="admin-tab-settings" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Site Settings</Link>
            )}
            <button onClick={logout} data-testid="admin-users-logout-btn" className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-3">Team Access</p>
          <h1 className="font-display text-3xl sm:text-4xl text-cream">Admin users</h1>
          <p className="text-sm text-faded mt-2">Admins can manage everything. Staff can view enquiries and packages.</p>
        </div>

        <form onSubmit={createUser} data-testid="create-user-form" className="rounded-3xl border border-gold/20 bg-card/70 p-6 sm:p-8 mb-10">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-5 flex items-center gap-2">
            <UserPlus size={14} /> Create new user
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Name *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" className="field-input" placeholder="Riya Patel" />
            </div>
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="field-input" placeholder="riya@arihantholiday.com" />
            </div>
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Password * (min 6 chars)</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" className="field-input" placeholder="••••••••" />
            </div>
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Role</label>
              <SelectField value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="new-user-role" options={[{ value: "staff", label: "Staff" }, { value: "admin", label: "Admin" }]} />
            </div>
          </div>
          <button type="submit" disabled={creating} data-testid="create-user-btn" className="flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60">
            <UserPlus size={15} /> {creating ? "Creating…" : "Create User"}
          </button>
        </form>

        {loading ? (
          <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-card/60 animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="admin-user-list">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-row-${u.id}`} className="rounded-2xl border border-cream/10 bg-card/60 px-6 py-4 flex items-center gap-4">
                <span className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold">
                  <Users size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-cream">{u.name}</p>
                  <p className="text-xs text-faded truncate">{u.email}</p>
                </div>
                <span className={`text-[10px] font-mono tracking-widest uppercase px-3 py-1 rounded-full border ${u.role === "admin" ? "text-gold border-gold/40 bg-gold/10" : "text-mist border-cream/20"}`}>
                  {u.role}
                </span>
                <button
                  onClick={() => removeUser(u)}
                  data-testid={`delete-user-${u.id}`}
                  className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center text-mist hover:border-red-400 hover:text-red-400 transition-colors"
                  title="Delete user"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
